import { ESdChangeReason, IConfig, IImageLibraryDto, ViewPart } from "@kuika/kuika-cl-model";
import { ImageLibraryService } from "../../../services/image-library-service";
import { IWithSDProps, withSD } from "../../../shared/hoc/screen-design-consumer";
import { PureSdComponent } from "../../../shared/pure-sd";
import { LoadingPresentationBase, resolveLoadingPresentation } from "../../../shared/utility/first-load-skeletons";
import { KMainFunctions } from "../../../shared/utility/main-functions";
import { DeleteModal } from "../../common/delete-modal";
import KuikaButton from "../../ui-kit/kuika-button/kuika-button";
import { KuikaSkeletonForm, KuikaSkeletonRegion } from "../../ui-kit/kuika-skeleton/kuika-skeleton";
import KuikaSpinnerSmall from "../../ui-kit/kuika-spinner-small/kuika-spinner-small";
import KuikaSvg from "../../ui-kit/kuika-svg/kuika-svg";
import { ConfigContainer } from "./app-settings-config-manager/app-settings-config-container";
import { ConfigManagerLanding } from "./config-manager-landing";
import { ConfigNameDialog, ConfigNameDialogState } from "./config-name-dialog";
import { buildConfigRowActions } from "./config-row-actions";
import { ProjectSettingsContainer } from "./project-settings/project-settings-container";
import { isSourceRepositoryVisible } from "./project-settings/source-repository-feature-gate";

export interface IAppSettingsCanvasState {
  allImage: IImageLibraryDto[];
  /** The row a delete has been requested for; the confirmation is owned here. */
  configToDelete?: IConfig;
  /** The row whose duplicate is in flight, so its own actions trigger reports it. */
  duplicatingId?: string;
  /** The name sheet the editor switcher's Rename / "create from global" opened. */
  nameDialog: ConfigNameDialogState;
}

export interface IAppSettingsCanvasProps {}

/**
 * The Config Manager's canvas: the landing list, the configuration editor, or the project
 * settings page — one surface at a time, exactly as before.
 *
 * The landing replaced the Configurations LEFT PANEL, so everything that panel could do is
 * routed from here: opening a configuration, creating one (including from a workspace
 * configuration), duplicate, delete, the search and its persistence, and the Source
 * Repository entry, which is now a header action rather than a row in a list of
 * configurations it was never one of.
 *
 * The panel deferred every one of those through the unsaved-changes callback because it sat
 * BESIDE an open configuration. The landing cannot: it only renders when nothing is open
 * (see `renderContent`), so there is never a dirty form to ask about. The editor keeps that
 * handshake for itself.
 */
class AppSettingsCanvas extends PureSdComponent<IAppSettingsCanvasProps & IWithSDProps, IAppSettingsCanvasState> {
  /** The project whose configurations are already loaded; guards the fetch against remounts. */
  loadedProjectId?: string;

  constructor(props: IAppSettingsCanvasProps & IWithSDProps) {
    super(props);
    this.state = {
      allImage: [],
      configToDelete: undefined,
      duplicatingId: undefined,
      nameDialog: { kind: "closed" }
    };
  }

  getImageLibrary = async (): Promise<void> => {
    try {
      const res = await ImageLibraryService.GetAll();
      if (res && res.status === 200) {
        this.setState({ allImage: res.data });
      }
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
    }
  };

  componentDidMount(): void {
    this.getImageLibrary();
    this.ensureConfigsLoaded();
  }

  componentDidUpdate(): void {
    this.ensureConfigsLoaded();
    // `duplicateConfiguration` is fire-and-forget: it flips the page loading flag and
    // refetches. When that settles the row is no longer busy, whether the copy landed or
    // the request failed — leaving the id set would spin one row forever.
    if (this.state.duplicatingId && !this.props.sd.isConfigPageLoading) {
      this.setState({ duplicatingId: undefined });
    }
  }

  /**
   * Load the list once per project, and never behind a page lock.
   *
   * This used to refetch on every mount with `shouldLockPage: true`, so returning to the
   * tool greyed the whole page out for a request whose answer was already in the provider —
   * the one tool in the shell that did. The API Builder's shape instead: fetch when the
   * project changes, and let mutations keep the list fresh (every create, duplicate and
   * delete already refetches through `ConfigManagerProvider`).
   */
  ensureConfigsLoaded = (): void => {
    const projectId: string | undefined = this.props.sd?.project?.id;
    if (!projectId || this.loadedProjectId === projectId) return;
    this.loadedProjectId = projectId;
    this.props.sd.fetchConfigs({ shouldLockPage: false, shouldFetchGlobals: true });
  };

  handleOpenConfig = (config: IConfig): void => {
    // `cancel…` first for its side effect, not for the editor: it also clears
    // `isProjectSettingsActive`, so opening a configuration leaves that page behind.
    this.props.sd.cancelCreatingOrEditingConfiguration();
    this.props.sd.setActiveConfiguration(config);
  };

  handleCreateConfig = (name: string, fromGlobal?: IConfig): void => {
    this.props.sd.setCreateConfigVisibilityOn(name, fromGlobal);
  };

  handleDuplicateConfig = (config: IConfig): void => {
    if (!config.id) return;
    this.setState({ duplicatingId: config.id });
    this.props.sd.duplicateConfiguration(config);
  };

  handleRenameConfig = (config: IConfig, name: string): Promise<boolean> =>
    this.props.sd.editConfiguration({ ...config, name });

  handleDeleteConfig = (): void => {
    const target: IConfig | undefined = this.state.configToDelete;
    if (!target?.id) return;
    this.props.sd.deleteConfiguration(target.id);
  };

  handleSearchTextChange = (searchText: string): void => {
    // The panel's own store key, so a query survives leaving the tool and coming back.
    this.props.sd.setSearchTexts(ViewPart.ConfigManagerPanel, searchText);
  };

  renderHeaderActions = (): React.ReactNode => {
    if (!isSourceRepositoryVisible()) return undefined;
    return (
      <KuikaButton
        type="secondary"
        size="md"
        data-testid="projectSettingsButton"
        onClick={() => this.props.sd.setProjectSettingsActive(true)}
      >
        <KuikaSvg name="git-commit" width={16} height={16} />
        Project Settings
      </KuikaButton>
    );
  };

  renderContent = (): React.ReactNode => {
    if (!this.props.sd?.activeConfiguration && !this.props.sd.isCreateConfigActive) {
      return (
        <ConfigManagerLanding
          scope="project"
          projectId={this.props.sd?.project?.id}
          configs={this.props.sd.configurations}
          isConfigListLoading={this.props.sd.isConfigListLoading}
          busyConfigId={this.state.duplicatingId}
          initialSearchText={this.props.sd.searchTexts[ViewPart.ConfigManagerPanel]}
          onSearchTextChange={this.handleSearchTextChange}
          onOpenConfig={this.handleOpenConfig}
          onCreateConfig={this.handleCreateConfig}
          onDuplicateConfig={this.handleDuplicateConfig}
          onRenameConfig={this.handleRenameConfig}
          onRequestDelete={(config: IConfig) => this.setState({ configToDelete: config })}
          headerActions={this.renderHeaderActions()}
        />
      );
    }

    return (
      <ConfigContainer
        rowActions={buildConfigRowActions({
          scope: "project",
          onCreateFromGlobal: (source: IConfig) => this.setState({ nameDialog: { kind: "createFromGlobal", source } }),
          onRename: (row: IConfig) => this.setState({ nameDialog: { kind: "rename", row } }),
          onDuplicate: this.handleDuplicateConfig,
          onRequestDelete: (config: IConfig) => this.setState({ configToDelete: config })
        })}
        allImage={this.state.allImage}
        handleGetImageLibrary={() => {
          this.getImageLibrary();
        }}
      />
    );
  };

  /**
   * What the page lock draws. `isConfigPageLoading` rises for a load AND for every duplicate,
   * delete, rename and settings save, so it is read against whether the canvas has anything of
   * its own to show: with nothing, the form's outline; with configurations — or an editor open,
   * which always has its own and must never be unmounted from under its unsaved form — the
   * content stays, dimmed and held still until the refetch is back.
   */
  getLoadingPresentation = (): LoadingPresentationBase => {
    const isEditorOpen: boolean = !!this.props.sd?.activeConfiguration || this.props.sd.isCreateConfigActive;
    const hasConfigurations: boolean = (this.props.sd.configurations?.length ?? 0) > 0;
    return resolveLoadingPresentation(this.props.sd.isConfigPageLoading, isEditorOpen || hasConfigurations);
  };

  render() {
    const presentation: LoadingPresentationBase = this.getLoadingPresentation();

    return (
      <>
        {presentation === "skeleton" ? (
          <KuikaSkeletonRegion label="Loading configurations" className="kuika-config-canvas__skeleton">
            <KuikaSkeletonForm />
          </KuikaSkeletonRegion>
        ) : (
          <KuikaSpinnerSmall spinning={presentation === "refreshing"}>{this.renderContent()}</KuikaSpinnerSmall>
        )}
        {/* Project settings are a DIALOG, not a third canvas surface. They are a handful of
            fields about where the project pushes its source, and taking over the page for
            them lost the list behind an entry that is not a configuration at all. The sheet
            belongs to the dialog itself, so its Save can sit in a real footer. */}
        <ProjectSettingsContainer
          visible={this.props.sd.isProjectSettingsActive}
          onClose={() => this.props.sd.setProjectSettingsActive(false)}
        />
        {/* The editor switcher's Rename and "create from global" land here: the landing owns
            its own sheet, but it is not mounted while a configuration is open. */}
        <ConfigNameDialog
          scope="project"
          configs={this.props.sd.configurations ?? []}
          dialog={this.state.nameDialog}
          onClose={() => this.setState({ nameDialog: { kind: "closed" } })}
          onCreateConfig={this.handleCreateConfig}
          onRenameConfig={this.handleRenameConfig}
        />
        {/* Owned here rather than by the landing, so the confirmation survives the row
            disappearing under it when the list refetches. */}
        <DeleteModal
          itemName={this.state.configToDelete?.name ?? ""}
          triggerModal={!!this.state.configToDelete}
          deleteItem={this.handleDeleteConfig}
          deleteType={"Configuration"}
          closeModal={() => this.setState({ configToDelete: undefined })}
        />
      </>
    );
  }

  checkNeedRender = (_changeReason: ESdChangeReason): boolean => {
    return false;
  };
}

const appSettingsCanvas = withSD<IAppSettingsCanvasProps>(AppSettingsCanvas);
export { appSettingsCanvas as AppSettingsCanvas };
